import React, { Dispatch, FC, SetStateAction } from "react";

import { FaIcons } from "@in/component-library";
import { KeyedMutator } from "swr";
import { deleteApplication } from "../../apis/delete-application";
import { useLayout } from "../../hooks/use-layout";
import { Application } from "../../models/application";
import {
  ActionModalConfirmation,
  ActionModalInfo,
  ApplicationWithCategoryType
} from "../../pages/applications/applications";
import ActionTool from "../action-tool/action-tool";

interface DeleteApplicationToolProps {
  application: ApplicationWithCategoryType;
  setActionModalInfo: Dispatch<SetStateAction<ActionModalInfo>>;
  setActionModalConfirmation: Dispatch<SetStateAction<ActionModalConfirmation>>;
  refetchApplications: KeyedMutator<Application[]>;
  iconButton?: boolean;
}

const DeleteApplicationTool: FC<DeleteApplicationToolProps> = ({
  application,
  setActionModalConfirmation,
  setActionModalInfo,
  refetchApplications,
  iconButton
}) => {
  const { translations } = useLayout();

  const handleDelete = async () => {
    const response = await deleteApplication(application.deleteUrl);

    const completedTextParts = translations.applicationsModalDeletionCompletedText.split(/\{applicationTitle\}/g);
    const failedTextParts = translations.applicationsModalDeletionFailedText.split(/\{applicationTitle\}/g);

    const textContent = (textParts: string[], applicationTitle: string) => {
      return (
        <p>
          {textParts[0]}
          <b>{applicationTitle}</b>
          {textParts[1]}
        </p>
      );
    };

    setActionModalConfirmation((prevstate) => ({
      ...prevstate,
      isProcessing: false,
      open: false
    }));

    if (response.code === 200) {
      setActionModalInfo({
        show: true,
        title: translations.applicationsModalDeletionCompletedTitle,
        content: textContent(completedTextParts, application.title)
      });
      refetchApplications();
    } else {
      setActionModalInfo({
        show: true,
        title: translations.applicationsModalDeletionFailedTitle,
        content: textContent(failedTextParts, application.title)
      });
    }
  };

  const handleConfirmDelete = () => {
    setActionModalConfirmation({
      open: true,
      title: translations.applicationsModalConfirmDeletionTitle,
      text: translations.applicationsModalConfirmDeletionText,
      confirmText: translations.applicationsActionDelete,
      cancelText: translations.cancel,
      onConfirm: () => {
        setActionModalConfirmation((prevstate) => ({
          ...prevstate,
          isProcessing: true
        }));
        handleDelete();
      },
      isProcessing: false
    });
  };

  return (
    <ActionTool
      onClick={handleConfirmDelete}
      iconName={FaIcons.TrashLight}
      label={translations.applicationsActionDelete}
      iconButton={iconButton}
      ariaLabel={`${translations.applicationsActionDelete} ${application.title}`}
    />
  );
};

export default DeleteApplicationTool;
